// 9fbef606107a605d69c0edbcd8029e5d
import {
  getImagesToDisplay,
  getMapSliceForColorProductId,
} from '@tcp/core/src/components/features/browse/ProductListing/molecules/ProductList/utils/productsCommonUtils';
import {
  getSiteId,
  isClient,
  getViewportInfo,
  routerPush,
  isCanada,
  getDateObject,
} from '@tcp/core/src/utils';
import { getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import { renderLabel } from '@tcp/core/src/utils/labels/labelUtilty';
import { getSearchBarLabels, lblCategoryMatches } from '../../labels/searchBar.labels';
import { SLASH_C_PATH } from '../../../../../../constants/plp.constants';

const globalSearchBarLabels = getSearchBarLabels();

const changeCaseFirstLetter = (params) => {
  if (typeof params === 'string') {
    return params.charAt(0).toUpperCase() + params.slice(1);
  }
  return null;
};

const suggestionFound = (searchResults) => {
  let isFound = false;
  if (searchResults && searchResults.autosuggestList) {
    searchResults.autosuggestList.forEach((type) => {
      if (type.suggestions && type.suggestions.length > 0) {
        isFound = true;
      }
    });
  }
  return isFound;
};

const trendingProductsExist = (searchResults, searchInput) => {
  let results = null;
  if (
    searchResults?.autosuggestProducts &&
    searchResults?.autosuggestProducts?.[0]?.imageUrl !== 'undefined/undefined' &&
    searchInput.current?.value !== undefined
  ) {
    results = searchResults;
  }
  return results;
};

const processSearchResult = (searchResults) => {
  let heading = '';
  let suggestionList = [];
  const categoryMatchesLabelText = renderLabel(
    globalSearchBarLabels[lblCategoryMatches],
    lblCategoryMatches
  );

  searchResults.autosuggestList.forEach((item) => {
    heading = item?.heading || '';
    item.suggestions.forEach((itemData, index) => {
      const isCategory = heading === categoryMatchesLabelText;
      let itemUrl = '';
      let toPath = `/${getSiteId()}/search/${itemData.text}`;
      if (isCategory) {
        itemUrl = itemData?.url.replace(/'/g, '');
        toPath = `/${getSiteId()}${itemUrl}`;
      }
      suggestionList = [
        ...suggestionList,
        {
          ...itemData,
          id: `${itemData.text.split(' ').join('_')}_${index}`,
          toPath,
          itemUrl,
        },
      ];
    });
  });
  return { heading, suggestionList };
};

const processTrendingSearchResults = (trendingResults) => {
  const trendingResultsFiltered = trendingResults?.autosuggestList?.[0]?.suggestions
    .filter(
      (suggestion) =>
        suggestion?.docType === 'TOP_SEARCH_QUERIES' || suggestion?.docType === 'KEYWORD_SUGGESTION'
    )
    .map((result) => {
      return changeCaseFirstLetter(result?.text);
    });
  return trendingResultsFiltered?.slice(0, 8);
};

const getCMSTrendingResults = (cmsTrendingSearches) => {
  if (cmsTrendingSearches === undefined || cmsTrendingSearches.length === 0) {
    return '';
  }
  return JSON.parse(cmsTrendingSearches)
    .map((item) => {
      return item;
    })
    .slice(0, 8);
};

const processImageUrl = (product) => {
  const imagePath = product?.imageUrl?.[0];

  if (imagePath) {
    const url = imagePath.split('/').pop();
    if (url?.includes('_')) {
      return `${url?.split('_')?.[0]}/${url}`;
    }
    return `${url?.split('.')?.[0]}/${url}`;
  }
  return '';
};

const processPrevImageUrl = (imgdata) => {
  let prevUrl;
  const facetnames = imgdata?.value[0]?.map((item) => {
    return item?.[0]?.facetName;
  });
  if (imgdata) {
    prevUrl = `/search/${imgdata?.key}?${imgdata?.value?.[0]
      ?.map((item, index) => {
        return (
          (typeof facetnames?.[index] === 'string' ? `${facetnames?.[index]}=` : '') +
          item
            ?.map((item1, index1) => {
              return index1 >= imgdata?.value?.[0]?.[index]?.length - 1
                ? `${item1?.id}&`
                : `${item1?.id}${encodeURIComponent(',')}`;
            })
            ?.join('')
        );
      })
      ?.join('')}`;
  }
  return prevUrl;
};

const processFeaturedProductResults = (trendingResults) => {
  const results = trendingResults;
  const siteId = getSiteId();
  const featuredProducts = results?.autosuggestProducts?.map((product) => {
    const productProps = { ...product };
    productProps.imageUrl = processImageUrl(product);
    productProps.originalPrice = product?.lowListPrice;
    productProps.offerPrice = product?.offerPrice;
    productProps.productName = product?.name;
    productProps.ratings = `${product?.rating}`;
    productProps.reviewCount = `${product?.reviewCount}`;
    productProps.pdpUrl = `${product?.productUrl}`;
    productProps.toPath = `/${siteId}${product?.productUrl}`;
    productProps.offerText = isCanada()
      ? product?.TCPMerchantTagCanadaStore || ''
      : product?.TCPMerchantTagUSStore || '';
    return productProps;
  });
  const isTabletFlag = isClient() ? getViewportInfo().isTablet : null;
  const featureProductLength = isTabletFlag ? 3 : 4;
  return featuredProducts?.slice(0, featureProductLength);
};

const getIsMobileWebSearch = () => {
  const { isMobile } = getViewportInfo() || {};
  return isClient() && isMobile;
};

const getIsTabletWebSearch = () => {
  const { isTablet } = getViewportInfo() || {};
  return isClient() && isTablet;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const navigateToUrl = (isRedirection, redirectUrl, routeTerm) => {
  if (isRedirection) {
    if (redirectUrl?.includes(window.location.hostname)) {
      if (redirectUrl?.includes('/p/')) {
        const pdpURL = redirectUrl.split('/p')[1];
        routerPush(`/p/?pid=${pdpURL}`, `/p/${pdpURL}`, { shallow: true });
      }
      if (redirectUrl?.includes('/c/')) {
        const plpURL = redirectUrl.split('/c')[1];
        routerPush(`${SLASH_C_PATH}cid=${plpURL}`, `/c/${plpURL}`, { shallow: true });
      }
      if (redirectUrl?.includes('/content/')) {
        const contentURL = redirectUrl.split('/content')[1];
        routerPush(`/content?contentType=${contentURL}`, `/content/${contentURL}`, {
          shallow: true,
        });
      }
      if (redirectUrl?.includes('/search/')) {
        const searchURL = redirectUrl.split('/search')[1];
        routerPush(`/search?searchQuery=${searchURL}`, `/search/${searchURL}`, {
          shallow: true,
        });
      }
    } else {
      window.location.href = redirectUrl;
    }
  } else {
    routerPush(
      `/search?searchQuery=${routeTerm}&searchType=trending search`,
      `/search/${routeTerm}`,
      { shallow: true }
    );
  }
};

const navigatePrevSearch = (searchName, searchUrl) => {
  routerPush(
    `/search?searchQuery=${searchName}&searchType=previous products`,
    `/search/${searchUrl?.split('/search/')?.[1]}`,
    { shallow: true }
  );
};

const generateFilterText = (item1, prevdata) => {
  const prevData = prevdata;
  if (item1.facetName === 'TCPColor_uFilter') {
    prevData.color += `${prevData?.color ? '&' : 'Color'} ${
      item1?.displayName
        ? `${item1?.displayName?.[0].toUpperCase()}${item1?.displayName?.toLowerCase()?.slice(1)}`
        : ''
    } `;
  } else if (item1.facetName === 'v_tcpsize_uFilter') {
    prevData.size += `${prevData?.size ? '&' : 'Size'} ${
      item1.displayName
        ? `${item1?.displayName?.[0]?.toUpperCase()}${item1.displayName?.slice(1)}`
        : ''
    } `;
  } else if (item1.facetName === 'gender_uFilter') {
    prevData.gender += `${prevData?.gender ? '&' : 'for'} ${
      item1.displayName
        ? `${item1?.displayName?.[0]?.toUpperCase()}${item1?.displayName?.slice(1)}`
        : ''
    } `;
  }
  return prevData;
};

const processPreviousSearchesResults = () => {
  let prevSearch = JSON.parse(getLocalStorage('prevSearch')) || '';
  prevSearch = Object.entries(prevSearch)?.map(([key, value]) => ({ key, value }));
  prevSearch = prevSearch.sort(
    (a, b) => getDateObject(b?.value?.[1]) - getDateObject(a?.value?.[1])
  );
  let prevdata = {};
  return prevSearch?.map((searchterm, index) => {
    prevdata.searchName = searchterm.key;
    const images = searchterm?.value?.[2];
    prevdata.imageUrl = images;
    prevdata.color = '';
    prevdata.size = '';
    prevdata.gender = '';
    const filterinfo = searchterm?.value?.[0]?.map((filters) => {
      return filters.map((filterdata) => {
        return filterdata;
      });
    });
    filterinfo.forEach((item) => {
      if (item.length > 0) {
        item.forEach((item1) => {
          prevdata = generateFilterText(item1, prevdata);
        });
      }
    });
    return {
      id: [1, 2, 3],
      searchName: prevdata.searchName,
      gender: prevdata.gender,
      size: prevdata.size,
      color: prevdata.color,
      imageUrl: prevdata.imageUrl,
      searchUrl: processPrevImageUrl(prevSearch?.[index])?.substring(
        0,
        processPrevImageUrl(prevSearch?.[index])?.length - 1
      ),
      navigatePrevSearch,
    };
  });
};
const processShowIAmLookingForProductList = (searchResults, searchInput) => {
  const heading = searchResults?.autosuggestList[0].heading;
  const results = trendingProductsExist(searchResults, searchInput);
  const autosuggestProducts = processFeaturedProductResults(results);
  return { heading, autosuggestProducts };
};

const getTopSearchTextQuery = (trendingResults) => {
  const trendingTextList = processTrendingSearchResults(trendingResults);
  let searchQuery = trendingTextList?.[0];
  if (searchQuery) {
    searchQuery = encodeURI(searchQuery);
    const siteId = getSiteId();
    return {
      URI: 'search',
      searchQuery,
      url: `/${siteId}/search/${searchQuery}`,
      numberOfProducts: 4,
    };
  }
  return null;
};

const processTrendingSearchProducts = (trendingSearchResult) => {
  const { loadedProductsPages } = trendingSearchResult || {};
  const siteId = getSiteId();

  if (loadedProductsPages && loadedProductsPages.length > 0) {
    const productArr = loadedProductsPages[0];
    if (productArr && productArr.length > 0) {
      return productArr.map((product, i) => {
        const { imagesByColor, colorsMap, skuInfo } = product;
        const itemColorProductId = skuInfo?.colorProductId;
        const colorProductId = colorsMap ? colorsMap[0].colorProductId : itemColorProductId;
        const curentColorEntry = getMapSliceForColorProductId(colorsMap, colorProductId);
        const imageUrls = getImagesToDisplay({
          imagesByColor,
          curentColorEntry,
          isAbTestActive: true,
          isFavoriteView: false,
        });
        const {
          productInfo: { ratings, reviewsCount, pdpUrl, listPrice, offerPrice, primaryBrand },
        } = product;
        const currentColorMiscInfo = curentColorEntry && curentColorEntry?.miscInfo;
        const { badge3 } = currentColorMiscInfo;
        return {
          id: `${product?.productInfo?.generalProductId}_${i}`,
          productName: product?.productInfo?.name,
          imageUrl: imageUrls?.[0],
          originalPrice: listPrice,
          offerPrice,
          ratings: `${ratings}`,
          reviewCount: `${reviewsCount}`,
          pdpUrl: `${pdpUrl}`,
          toPath: `/${siteId}${pdpUrl}`,
          offerText: badge3 !== null ? badge3 : '',
          primaryBrand,
        };
      });
    }
  }
  return [];
};

export {
  changeCaseFirstLetter,
  suggestionFound,
  processSearchResult,
  getIsMobileWebSearch,
  processTrendingSearchResults,
  processFeaturedProductResults,
  navigateToUrl,
  getIsTabletWebSearch,
  processPreviousSearchesResults,
  processPrevImageUrl,
  processShowIAmLookingForProductList,
  trendingProductsExist,
  getTopSearchTextQuery,
  getCMSTrendingResults,
  processTrendingSearchProducts,
  navigatePrevSearch,
  processImageUrl,
};
